import { Loader } from "@googlemaps/js-api-loader";
import { displayToast } from "../modules/utils";
import { createPopper } from "@popperjs/core";

export default class AutocompleteGoogle {
  constructor(node) {
    this.element = node;

    this.searchInput = this.element.querySelector(".js-autocomplete-google-input");
    this.searchInputWrapper = this.element.querySelector(".js-autocomplete-google-input-wrapper");
    this.formWrapper = this.element.querySelector(".js-autocomplete-google-form-wrapper");
    this.countryInput = this.element.querySelector(".js-autocomplete-google-country-input");
    this.autocompleteToggleWrapper = this.element.querySelector(".js-autocomplete-google-toggle-wrapper");
    this.autocompleteToggle = this.element.querySelector(".js-autocomplete-google-toggle");
    this.inputs = this.element.querySelectorAll(".js-autocomplete-google-inputs");
    this.customInputs = this.element.querySelectorAll(".js-autocomplete-google-custom-inputs");
    this.addressesWrapper = this.element.querySelector(".js-autocomplete-google-addresses-wrapper");
    this.addressOptionTemplate = this.element.querySelector(".js-autocomplete-google-address-option-template");
    this.noResultsTemplate = this.element.querySelector(".js-autocomplete-google-no-results-template");
    this.extraAddressLineWrapper = this.element.querySelector(".js-autocomplete-google-extra-address-line-wrapper");

    // Hardcoded since its from google's API
    this.dataCountryFieldKey = "country";
    this.dataRegionFieldKeys = ["administrative_area_level_1", "administrative_area_level_2", "administrative_area_level_3", "administrative_area_level_4"];

    this.results = {}; // Remembers the results of the search
    this.timeoutThrottle = null;
    this.boundFunction = this.checkIfWithinBounds.bind(this);
    this.boundKeyFunction = this.handleKeydown.bind(this);
    this.addedDropdownEventListeners = false;
    this.region = this.countryInput.dataset.valueCode || "nl";
    this.language = this.element.dataset.language;
    this.hideFieldsCountries = this.element.dataset.hideFieldsForCountries.split(",");

    this.arrowKeysIndex = 0;

    this.loader = new Loader({
      apiKey: this.element.dataset.googleApiKey,
      version: "weekly",
      libraries: ["places"],
    });

    this.initialize();
  }

  initialize() {
    this.countryInput.addEventListener("change", (e) => {
      this.region = e.target.dataset.valueCode;

      this.extraAddressLineWrapper.classList.toggle("d-none", this.hideFieldsCountries.includes(e.target.dataset.valueCode));
    });

    window.addEventListener("google-maps-api-failure", () => {
      // We revert google's changes to the inputs caused by the authentication failure.
      this.enabled = false;
      displayToast(this.element.dataset.errorGoogleMessage, "warning");

      this.toggleFormAvailability();
    });

    window.addEventListener("beyuna-classes-initialized", () => {
      this.regionSelectInstance = window.Beyuna.objects["region-select"].autocomplete;
    });

    this.autocompleteToggle.addEventListener("click", () => {
      this.enabled = false;
      this.autocompleteToggleWrapper.classList.add("d-none");

      document.removeEventListener("click", this.boundFunction);
      document.removeEventListener("keydown", this.boundKeyFunction);
      this.addedDropdownEventListeners = false;

      this.toggleFormAvailability();
    });

    this.initializeGoogleAutocomplete();
  }

  initializeGoogleAutocomplete() {
    this.loader.load().then(() => {
      this.searchInput.addEventListener("input", (e) => {
        if (this.timeoutThrottle) {
          clearTimeout(this.timeoutThrottle);
        }

        // Fix for wrong position when used within a modal
        if (!this.dropdownPositionSet) {
          this.setDropdownPosition();
        }

        this.timeoutThrottle = setTimeout(() => {
          const value = e.target.value;
          this.results = {}; // Clear the results

          if (value == "") {
            this.addressesWrapper.innerHTML = "";
            return;
          }

          this.sendRequest(value);
        }, 300);
      });
    });
  }

  sendRequest(value) {
    this.searchInputWrapper.classList.add("loading");

    // We must generate new session tokens for each request
    const request = {
      input: value,
      region: this.region,
      includedPrimaryTypes: ["route", "street_address", "postal_code", "street_number"], // Limit results to these types
      language: this.language,
      sessionToken: new google.maps.places.AutocompleteSessionToken(),
    };

    google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then(({ suggestions }) => {
      Promise.all(
        suggestions.map(async (suggestion, index) => {
          const placePrediction = suggestion.placePrediction;
          const { place } = await placePrediction.toPlace().fetchFields({
            fields: ["addressComponents", "formattedAddress"],
          });

          this.results[index] = {
            addressComponents: place.addressComponents,
            formattedAddress: place.formattedAddress,
          };
        })
      ).then(() => {
        this.arrowKeysIndex = 0; // Reset the arrow keys index
        this.showAddressOptions();
        this.searchInputWrapper.classList.remove("loading");
      });
    });
  }

  toggleFormAvailability() {
    this.inputs.forEach((input) => {
      input.readOnly = this.enabled;
    });

    this.customInputs.forEach((input) => {
      input.classList.toggle("disabled", this.enabled);
    });
  }

  populateForm(address) {
    this.addressesWrapper.innerHTML = "";

    this.formWrapper.querySelectorAll("input").forEach((input) => {
      input.value = "";
    });

    const addressKeys = address.map((address) => address.types[0]);
    const inputs = this.element.querySelectorAll("[data-field]");

    inputs.forEach((input) => {
      input.dataset.field.split(",").forEach((field) => {
        const index = addressKeys.indexOf(field);

        if (index === -1) return; // Skip if the field is not found in the address

        const value = address[index].longText;

        if (field === this.dataCountryFieldKey) {
          if (!this.regionSelectInstance.setCountry(address[index].shortText, true)) {
            displayToast(this.element.dataset.noCountryFound, "warning");
          } else {
            this.handleRegionFetch(address);
          }
        } else {
          input.value = value;
        }
      });
    });

    this.enabled = false;
    this.autocompleteToggleWrapper.classList.add("d-none");
    this.toggleFormAvailability();
  }

  handleRegionFetch(address) {
    const addressKeys = address.map((address) => address.types[0]);
    window.addEventListener(
      "region-select-fetch-success",
      () => {
        // Since the country gets set first, we need to wait for the region fetch to finish before setting the region

        // We attempt 4 levels of administrative_area_level since some countries handle regions differently

        let regionSet = false;

        this.dataRegionFieldKeys.forEach((dataRegionFieldKey) => {
          if (regionSet) return;

          const regionKey = addressKeys.find((key) => key === dataRegionFieldKey);

          if (regionKey) {
            const regionIndex = addressKeys.indexOf(regionKey);
            regionSet = this.regionSelectInstance.setRegion(address[regionIndex].longText, true);
          }
        });

        if (!regionSet) displayToast(this.element.dataset.noRegionFound, "warning");
        regionSet = false; // Reset the regionSet flag
      },
      { once: true }
    );
  }

  populateAddressOption(scope, formattedAddress, country) {
    const element = scope.querySelector(".js-autocomplete-google-value");
    const countryElement = scope.querySelector(".js-autocomplete-google-country");

    if (element) {
      // Makes the search term bold
      const key = this.searchInput.value.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special characters
      const regex = new RegExp(key, "i");
      const match = formattedAddress.match(regex);
      element.innerHTML = formattedAddress.replace(regex, `<strong>${match}</strong>`);
    }

    if (countryElement) {
      countryElement.innerHTML = country;
    }
  }

  showAddressOptions() {
    if (!this.addedDropdownEventListeners) {
      document.addEventListener("click", this.boundFunction);
      document.addEventListener("keydown", this.boundKeyFunction);
      this.addedDropdownEventListeners = true;
    }

    this.addressesWrapper.innerHTML = "";

    if (Object.keys(this.results).length === 0) {
      const noResultsElement = this.noResultsTemplate.cloneNode(true);
      this.addressesWrapper.appendChild(noResultsElement.content);
      return;
    }

    Object.values(this.results).forEach((address, index) => {
      const addressOption = this.addressOptionTemplate.content.cloneNode(true);

      if (index === 0) {
        const label = addressOption.querySelector("label");
        label.classList.add("bg-gray-100");
      }

      const radio = addressOption.querySelector("input[type='radio']");
      const country = address.addressComponents.find((component) => component.types.includes(this.dataCountryFieldKey)).longText;

      this.populateAddressOption(addressOption, address.formattedAddress, country);

      radio.addEventListener("change", () => {
        this.searchInput.value = address.formattedAddress;
        this.populateForm(address.addressComponents);

        document.removeEventListener("click", this.boundFunction);
        document.removeEventListener("keydown", this.boundKeyFunction);
        this.addedDropdownEventListeners = false;
      });

      this.addressesWrapper.appendChild(addressOption);
    });
  }

  setDropdownPosition() {
    createPopper(this.searchInput, this.addressesWrapper, {
      placement: "bottom-start",
      modifiers: [
        {
          name: "sameWidth",
          enabled: true,
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          phase: "beforeWrite",
          requires: ["computeStyles"],
        },
        {
          name: "flip",
          options: {
            padding: {
              top: 16,
            },
          },
        },
        {
          name: "offset",
          options: {
            offset: [0, 16],
          },
        },
      ],
    });

    this.dropdownPositionSet = true;
  }

  checkIfWithinBounds(e) {
    const target = e.target;

    if (this.addressesWrapper == target) return;

    if (!this.addressesWrapper.contains(target)) {
      this.addressesWrapper.innerHTML = "";

      document.removeEventListener("click", this.boundFunction);
      document.removeEventListener("keydown", this.boundKeyFunction);
      this.addedDropdownEventListeners = false;
    }
  }

  handleKeydown(e) {
    const { key } = e;
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(key)) return;

    e.preventDefault();
    const maxIndex = this.addressesWrapper.children.length - 1;

    if (key === "ArrowDown") {
      this.arrowKeysIndex = this.arrowKeysIndex === maxIndex ? 0 : this.arrowKeysIndex + 1;
    } else if (key === "ArrowUp") {
      this.arrowKeysIndex = this.arrowKeysIndex === 0 ? maxIndex : this.arrowKeysIndex - 1;
    } else if (key === "Enter") {
      this.addressesWrapper.children[this.arrowKeysIndex]?.querySelector("input")?.click();
    }

    Array.from(this.addressesWrapper.children).forEach((child, index) => {
      child.classList.toggle("bg-gray-100", index === this.arrowKeysIndex);
    });
  }
}
